import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PaginatedResponse } from '@/lib/axios/types';
import { AgentMetricsData } from '../../agent-performance/agentTableTypes';

interface AgentMetricsTableFooterProps {
  pagedData: PaginatedResponse<AgentMetricsData> | null;
  currentPage: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  itemName?: string;
}

// Skeleton loading component for pagination
const PaginationSkeleton: React.FC = () => (
  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73] shrink-0">
    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-gray-200 animate-pulse rounded" />
      <div className="h-4 w-16 bg-gray-200 animate-pulse rounded" />
      <div className="w-8 h-8 bg-gray-200 animate-pulse rounded" />
    </div>
  </div>
);

export const AgentMetricsTableFooter: React.FC<AgentMetricsTableFooterProps> = ({
  pagedData,
  currentPage,
  isLoading = false,
  onPageChange,
}) => {
  if (isLoading) {
    return <PaginationSkeleton />;
  }
  if (!pagedData) {
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73] shrink-0">
        <div className="text-xs text-muted-foreground">No data available</div>
      </div>
    );
  }

  const {
    totalPages,
    total: totalItems,
    page: currentPageFromData,
    limit: itemsPerPageFromData,
  } = pagedData;

  // Calculate pagination display values
  const hasItems = totalItems > 0;
  const startIndex = hasItems ? (currentPageFromData - 1) * itemsPerPageFromData + 1 : 0;
  const endIndex = hasItems
    ? Math.min((currentPageFromData - 1) * itemsPerPageFromData + itemsPerPageFromData, totalItems)
    : 0;
  const displayText = hasItems ? `${startIndex}-${endIndex}` : '0';
  const pageText = hasItems ? `${currentPageFromData} / ${totalPages}` : '0 / 0';

  // Navigation handlers and states
  const canGoPrevious = hasItems && currentPageFromData > 1 && !isLoading;
  const canGoNext = hasItems && currentPageFromData < totalPages && !isLoading;

  const containerClasses =
    'flex items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73] shrink-0';
  const buttonClasses =
    'w-8 h-8 grid place-items-center rounded border disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors';

  return (
    <div className={containerClasses}>
      <div className="text-xs text-muted-foreground">
        Showing {displayText} of {totalItems}
      </div>
      <div className="flex items-center gap-2">
        <button
          className={buttonClasses}
          onClick={() => canGoPrevious && onPageChange(currentPageFromData - 1)}
          disabled={!canGoPrevious}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs px-1">{pageText}</span>
        <button
          className={buttonClasses}
          onClick={() => canGoNext && onPageChange(currentPageFromData + 1)}
          disabled={!canGoNext}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
